import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import { isFeatureEnabled } from "@/lib/featureFlags";
import { Brain,
  LayoutDashboard, Building2, Users, FileSignature, Briefcase, Clock,
  CalendarOff, Receipt, CreditCard, FileBarChart, FolderOpen,
  ShieldCheck, Sparkles, LogOut, FileSearch, Stethoscope, Calculator, ScanLine, NotebookPen, Mail,
  BookUser, ClipboardList, Shield, UserCheck, Sun, CalendarDays, FileDown, Inbox, MailWarning,
} from "lucide-react";

const NAV = [
  { to: "/app", icon: LayoutDashboard, label: "Tableau de bord", end: true, roles: "*" },
  { to: "/app/aujourdhui", icon: Sun, label: "Aujourd'hui", roles: ["salarie","admin","gestionnaire_ge"], flag: "lot3_aujourdhui" },
  { to: "/app/entreprises", icon: Building2, label: "Entreprises", roles: "*" },
  { to: "/app/registre-membres", icon: BookUser, label: "Registre des membres", roles: ["admin","gestionnaire_ge","finance","auditeur"] },
  { to: "/app/registre-salaries", icon: UserCheck, label: "Registre des salariés", roles: ["admin","gestionnaire_ge","finance","auditeur"] },
  { to: "/app/prospects", icon: Inbox, label: "Demandes simulateur", roles: ["admin","gestionnaire_ge"] },
  { to: "/app/conformite", icon: ShieldCheck, label: "Conformité & inspections", roles: ["admin","gestionnaire_ge","auditeur"] },
  { to: "/app/admin/courriels", icon: MailWarning, label: "Courriels non partis", roles: ["admin","gestionnaire_ge"], badge: "courriels" },
  { to: "/app/demandes", icon: FileSearch, label: "Demandes MAD", roles: ["admin","gestionnaire_ge","finance","employeur","auditeur"] },
  { to: "/app/salaries", icon: Users, label: "Salariés", roles: "*" },
  { to: "/app/conventions", icon: FileSignature, label: "Conventions", roles: "*" },
  { to: "/app/contrats", icon: FileSignature, label: "Contrats de travail", roles: ["admin","gestionnaire_ge","auditeur","salarie"] },
  { to: "/app/devis", icon: Calculator, label: "Devis", roles: ["admin","gestionnaire_ge","finance","auditeur"] },
  { to: "/app/missions", icon: Briefcase, label: "Missions", roles: "*" },
  { to: "/app/planning", icon: CalendarDays, label: "Planning", roles: "*" },
  { to: "/app/timesheets", icon: Clock, label: "Feuilles de temps", roles: "*" },
  { to: "/app/absences", icon: CalendarOff, label: "Absences & Congés", roles: "*" },
  { to: "/app/frais", icon: Receipt, label: "Frais", roles: "*" },
  { to: "/app/credits", icon: CreditCard, label: "Abonnements & Crédits", roles: ["admin","gestionnaire_ge","finance","employeur","auditeur"] },
  { to: "/app/appels", icon: FileBarChart, label: "Appels à contribution", roles: ["admin","gestionnaire_ge","finance","employeur","auditeur"] },
  { to: "/app/comptabilite", icon: Calculator, label: "Comptabilité & FEC", roles: ["admin","gestionnaire_ge","finance","auditeur"] },
  { to: "/app/preparation-paie", icon: Calculator, label: "Préparation paie", roles: ["admin","gestionnaire_ge","finance"], flag: "lot4_preparation_paie" },
  { to: "/app/export-profiles", icon: FileDown, label: "Profils d'export", roles: ["admin","gestionnaire_ge","finance"] },
  { to: "/app/ged", icon: FolderOpen, label: "GED — Documents", roles: "*" },
  { to: "/app/medical", icon: Stethoscope, label: "Espace médical", roles: ["admin","medical"] },
  { to: "/app/qr-scans", icon: ScanLine, label: "Vérifications QR", roles: ["admin","gestionnaire_ge","finance","auditeur"] },
  { to: "/app/notes", icon: NotebookPen, label: "Notes terrain", roles: "*" },
  { to: "/app/taches", icon: ClipboardList, label: "Tâches", roles: "*" },
  { to: "/app/assistant", icon: Sparkles, label: "ZOE — Assistante", roles: ["admin","gestionnaire_ge","finance","employeur","medical","auditeur"] },
  { to: "/app/lili", icon: Sparkles, label: "ZOE — Administration", roles: ["admin","gestionnaire_ge"] },
  { to: "/app/zoe-memoire", icon: Brain, label: "Mémoire de ZOE", roles: ["admin","gestionnaire_ge","finance","employeur","medical","auditeur","salarie","missionnaire"] },
  { to: "/app/emails", icon: Mail, label: "Emails Brevo", roles: ["admin","gestionnaire_ge","finance","auditeur"] },
  { to: "/app/roles", icon: Shield, label: "Rôles & permissions", roles: ["admin"] },
  { to: "/app/audit", icon: ShieldCheck, label: "Journal d'audit", roles: ["admin","gestionnaire_ge","auditeur"] },
  { to: "/app/cleanup-manifest", icon: ShieldCheck, label: "Manifeste nettoyage (lecture seule)", roles: ["admin"] },
  { to: "/app/admin/comptes", icon: Shield, label: "Comptes utilisateurs", roles: ["admin"] },
  { to: "/app/admin/feature-flags", icon: Shield, label: "Interrupteurs fonctionnalités", roles: ["admin"] },
];

const ROLE_LABELS = {
  admin: "Administrateur",
  gestionnaire_ge: "Gestionnaire GE",
  finance: "Finance",
  employeur: "Employeur",
  salarie: "Salarié",
  missionnaire: "Missionnaire",
  medical: "Médical",
  auditeur: "Auditeur",
};

function useEchecsCount(enabled) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const load = () => api.get("/courriels/echecs/count").then((r) => setCount(r.data.count)).catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    window.addEventListener("courriels-updated", load);
    return () => { clearInterval(timer); window.removeEventListener("courriels-updated", load); };
  }, [enabled]);
  return count;
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const echecs = useEchecsCount(["admin", "gestionnaire_ge"].includes(user?.role));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const visible = NAV.filter((i) =>
    (i.roles === "*" || (user && i.roles.includes(user.role))) &&
    (!i.flag || isFeatureEnabled(i.flag))
  );

  return (
    <aside className="hidden lg:flex w-72 flex-col bg-slate-900 text-slate-300 sidebar-scroll" data-testid="sidebar">
      <div className="p-6 border-b border-slate-800 flex items-center justify-between gap-2">
        <Logo variant="light" />
        <NotificationBell />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        {visible.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`nav-${item.to.split("/").pop() || "dashboard"}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 ${
                isActive
                  ? "bg-slate-800/80 text-white border-l-2 border-[#C5A059] pl-[10px]"
                  : "hover:bg-slate-800/50 hover:text-white border-l-2 border-transparent"
              }`
            }
          >
            <item.icon className="h-4 w-4 shrink-0" />
            <span className="font-medium tracking-tight">{item.label}</span>
            {item.badge === "courriels" && echecs > 0 && (
              <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center"
                    data-testid="nav-courriels-badge" aria-label={`${echecs} courriels non partis`}>
                {echecs > 99 ? "99+" : echecs}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-800 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#C5A059] to-[#A88846] flex items-center justify-center text-white font-semibold">
            {user?.name?.charAt(0)?.toUpperCase() || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-white truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-xs text-slate-400 truncate">{ROLE_LABELS[user?.role] || user?.role}</div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-slate-800/60 hover:bg-slate-800 text-slate-300 hover:text-white transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Se déconnecter
        </button>
      </div>
    </aside>
  );
}
